import { Typography } from "@/design-system/Atoms";
import { ImageSlider } from "@/design-system/Molecules";
import { useScrollToSection } from "@/hooks/useScrollToSection";
import { useTestimonialsService } from "@/rtkQuery/features/hooks/useTestimonialsService";
import type { FC } from "react";
import { AppEmptyState } from "@/design-system/Feedbacks";
import { ShimmerEffect } from "@/design-system/Atoms";

interface ImageWithTextSectionProps {
  highlight?: string;
  heading?: string;
  variant?: "standard" | "highlighted";
  highlightedHeadingMain?: string;
  highlightedHeadingAccent?: string;
  sectionDescription?: string;
  showSwiper: boolean;
  imageUrl?: string;
  altText?: string;
}
export const ImageWithTextSection: FC<ImageWithTextSectionProps> = ({
  highlight,
  heading,
  variant,
  highlightedHeadingMain,
  highlightedHeadingAccent,
  sectionDescription,
  showSwiper,
  imageUrl,
  altText,
}) => {
  const {
    testimonialData,
    testimonialIsLoading,
    testimonialIsError,
  } = useTestimonialsService();

  const { ref } = useScrollToSection<HTMLElement>({
    behavior: "smooth",
    block: "start",
    offset: 80,
  });
  return (
    <section
      id="about-us-feature"
      ref={ref}
      className="mb-[75px] lg:mb-[160px] pt-40"
    >
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row gap-10 items-center justify-center">
          {/* Image */}
          <div className="lg:w-1/2 text-center">
            <div className="w-full max-w-[634px] overflow-hidden mx-auto">
              {" "}
              {/*  aspect-square  */}
              <img
                src={imageUrl}
                alt={altText}
                className="w-full h-full object-cover rounded-[20px]"
              />
            </div>
          </div>

          <div className="lg:w-1/2 w-full px-4 lg:pl-[9.333333%]">
            {highlight && (
              <Typography
                variant="categoryText"
                as="div"
                className="mb-[3.125rem]"
              >
                {highlight}
              </Typography>
            )}
            {testimonialIsLoading ? (
              <div>
                <div>
                  <ShimmerEffect className="h-[64px] w-full mb-10" />
                  <ShimmerEffect className="h-[100px] w-full mb-4 mt-0" />
                  <ShimmerEffect className="h-[27px] w-14 lg:w-28 mt-12" />
                </div>

                <div className="relative flex lg:flex py-4 mt-6 mb-1 justify-between items-center">
                  <div className="flex gap-7">
                    <ShimmerEffect className="h-[60px] w-[80px] lg:w-[80px]" />
                    <ShimmerEffect className="h-[60px] w-[80px] lg:w-[80px]" />
                  </div>
                </div>
              </div>
            ) : testimonialIsError ? (              
               <div className="w-fit mx-auto">
               <div className="rounded-[20px] min-h-[30vh] lg:min-h-[25vh] flex items-center justify-center">
                 <AppEmptyState message="No testimonials found." />
               </div>
             </div> 
            ) : (
              <>
                {variant === "highlighted" &&
                highlightedHeadingMain &&
                highlightedHeadingAccent ? (
                  <Typography
                    variant="sectionHeading"
                    as="h2"
                    className="mb-10"
                  >
                    <>
                      {highlightedHeadingMain}{" "}
                      <span className="text-gray-400">
                        {highlightedHeadingAccent}
                      </span>
                    </>
                  </Typography>
                ) : heading ? (
                  <Typography
                    variant="sectionHeading"
                    as="h2"
                    className="mb-10"
                  >
                    {heading}
                  </Typography>
                ) : null}

                {sectionDescription && (
                  <Typography
                    variant="sectionDescriptionLarge"
                    as="p"
                    className="mb-4 mt-0 leading-normal"
                  >
                    {sectionDescription}
                  </Typography>
                )}

                {showSwiper ? (
                  <ImageSlider testimonials={testimonialData ?? []} />
                ) : (
                  <></>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImageWithTextSection;
